<template>
  <Card class="trip-launch-guideline" :class="{ 'trip-launch-guideline__expanded': expanded }">
    <div class="trip-launch-guideline__header" @click="expanded = !expanded">
      <h1 class="trip-launch-guideline__title">Trip launch guideline</h1>
      <i class="icon" :class="expanded ? 'icon-arrow-up-1' : 'icon-arrow-down-1'" />
    </div>

    <div v-if="expanded" class="trip-launch-guideline__steps">
      <div v-for="(step, index) in steps" :key="index" class="step">
        <div class="step__indicator">
          <div
            class="step__circle"
            :class="{
              'step__circle--active': step.order === progress,
              'step__circle--completed': step.order < progress,
            }"
          />
          <div v-if="index < steps.length - 1" class="step__line" />
        </div>

        <div class="step__content">
          <div class="step__header">
            <span
              class="step__label"
              :class="{
                'step__label--active': step.order === progress,
                'step__label--completed': step.order < progress,
              }"
              >{{ step.label }}</span
            >
            <span v-if="step.order === progress" class="step__badge step__badge--active">You're here</span>
            <span v-else-if="step.order === progress + 1" class="step__badge step__badge--next">Next</span>
          </div>
          <p v-if="step.description && step.order >= progress" class="step__description">
            {{ step.description }}
          </p>
        </div>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Card from '@/components/Theme2/Card.vue'

interface Step {
  label: string
  order: number
  description?: string
}

const steps: Step[] = [
  {
    label: 'Customize your trip',
    description:
      'Fine-tune activities, accommodations, and pacing so the trip reflects your travel style and expertise.',
    order: 1,
  },
  {
    label: 'Set up booking details',
    description: 'Confirm dates, group size and pricing to get your traveler itinerary ready.',
    order: 2,
  },
  {
    label: 'Preview your booking page',
    description: 'See exactly what travelers will experience and make small adjustments to your information.',
    order: 3,
  },
  {
    label: 'Launch itinerary',
    description: 'Make it shareable so you can start promoting and get your first booking.',
    order: 4,
  },
]

const { progress, open } = defineProps<{
  progress: number
  open: boolean
}>()

const expanded = ref(open)
</script>
<style lang="scss" scoped>
.trip-launch-guideline {
  .trip-launch-guideline__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
  }

  .trip-launch-guideline__title {
    margin-block: 0;
    font-size: $text-xl;
  }

  .icon {
    color: $grey-100;
    font-size: $text-base;
  }

  .trip-launch-guideline__steps {
    margin-top: $spacing-2;
  }

  .step {
    display: flex;
    gap: $spacing-3;

    .step__indicator {
      display: flex;
      flex-direction: column;
      align-items: center;

      .step__circle {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid $grey-50;
        background-color: $white;
        flex-shrink: 0;
        margin-top: 6px; /* (24px header - 12px circle) / 2 = 6px */
      }

      .step__circle--active {
        border-color: $green-200;
        background-color: $green-200;
      }

      .step__line {
        width: 2px;
        flex: 1;
        min-height: 100%;
        background-color: $grey-25;
      }
    }

    &:last-child {
      .step__content {
        padding-bottom: 0;
      }
    }

    .step__content {
      padding-bottom: $spacing-5;
      flex: 1;

      .step__header {
        display: flex;
        align-items: center;
        gap: $spacing-2;
        min-height: 24px; /* Ensures consistent height whether badge is present or not */
      }

      .step__label {
        font-weight: $font-semibold;
        color: $grey-100;

        &--active {
          color: $black;
        }

        &--completed {
          color: $green-200;
        }
      }

      .step__badge {
        font-size: $text-xs;
        font-weight: $font-medium;
        padding: 2px $spacing-2;
        border-radius: $rounded-max;
      }

      .step__badge--active {
        background-color: $yellow-50;
      }

      .step__badge--next {
        background-color: $green-50;
      }

      .step__description {
        margin: $spacing-2 0 0 0;
        font-size: $text-sm;
        color: $grey-100;
        line-height: 1.5;
      }
    }
  }
}
</style>
